<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { differenceInDays } from 'date-fns';
import { useAlert, useTrack } from 'dashboard/composables';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { REPORTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { GROUP_BY_FILTER } from '../../constants';
import ReportContainer from '../../ReportContainer.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  since: { type: Date, required: true },
  until: { type: Date, required: true },
  businessHours: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();

const REPORT_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

// Mesmos limiares de dias que ReportFilters.vue usa para o agrupamento
// automático (ver fetchFilterItems) — aqui sem dropdown manual, já que o
// período é único para o Dashboard inteiro.
const groupBy = computed(() => {
  const days = differenceInDays(props.until, props.since);
  if (days >= 364) return GROUP_BY_FILTER[4];
  if (days >= 89) return GROUP_BY_FILTER[3];
  if (days >= 29) return GROUP_BY_FILTER[2];
  return GROUP_BY_FILTER[1];
});

const getRequestPayload = () => ({
  from: Math.floor(props.since.getTime() / 1000),
  to: Math.floor(props.until.getTime() / 1000),
  groupBy: groupBy.value.period,
  businessHours: props.businessHours,
});

const fetchAccountSummary = () => {
  try {
    store.dispatch('fetchAccountSummary', getRequestPayload());
  } catch {
    useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
  }
};

const fetchChartData = () => {
  Object.keys(REPORT_KEYS).forEach(async key => {
    try {
      await store.dispatch('fetchAccountReport', {
        metric: REPORT_KEYS[key],
        ...getRequestPayload(),
      });
    } catch {
      useAlert(t('REPORT.DATA_FETCHING_FAILED'));
    }
  });
};

const fetchAllData = () => {
  fetchAccountSummary();
  fetchChartData();
};

const downloadConversationReports = () => {
  const { from, to } = getRequestPayload();
  const fileName = generateFileName({
    type: 'conversation',
    to,
    businessHours: props.businessHours,
  });
  store.dispatch('downloadConversationsSummaryReports', {
    from,
    to,
    fileName,
    businessHours: props.businessHours,
  });
  useTrack(REPORTS_EVENTS.FILTER_REPORT, {
    filterValue: getRequestPayload(),
    reportType: 'conversations',
  });
};

watch(() => [props.since, props.until, props.businessHours], fetchAllData, {
  immediate: true,
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex justify-end">
      <Button
        :label="t('REPORT.DOWNLOAD_CONVERSATION_REPORTS')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadConversationReports"
      />
    </div>
    <ReportContainer :group-by="groupBy" />
  </div>
</template>
